import { cosemApi } from '@services/providers/cosem';
import { Ticketing, TicketingTable, TicketingToCreate } from './ticketing.types';
import { ApiPaginationResponse, ApiResponse } from '@services/providers/cosem.types';
import { ActiveFilter } from '@utils/filters';

const ticketingApi = cosemApi.injectEndpoints({
    endpoints: (build) => ({
        getTicketingEdition: build.mutation<
            ApiPaginationResponse<TicketingTable>,
            { limit?: number; page?: number; search?: string; orderBy?: string; filters?: ActiveFilter[] }
        >({
            query: ({ limit = 10, page = 1, search, orderBy, filters }) => {
                return {
                    url: `/member/ticketings/edition`,
                    method: 'GET',
                    params: { limit, page, search: search ?? '', orderBy: orderBy ?? '', filters: JSON.stringify(filters ?? []) }
                };
            },
            transformResponse: (response: ApiPaginationResponse<TicketingTable>) => response
        }),
        getTicketingEditionExport: build.mutation<ApiResponse<string>, {search?: string; orderBy?: string; filters?: ActiveFilter[] }>({
            query: ({ search, orderBy, filters }) => {
                return {
                    url: `/member/ticketings/edition/export`,
                    method: 'GET',
                    params: { search: search ?? '', orderBy: orderBy ?? '', filters: JSON.stringify(filters ?? []) }
                };
            },
            transformResponse: (response: ApiResponse<string>) => response
        }),
        getTicketingExport: build.mutation<ApiResponse<string>, { search?: string; orderBy?: string; filters?: ActiveFilter[] }>({
            query: ({ search, orderBy, filters }) => {
                return {
                    url: `/member/ticketings/export`,
                    method: 'GET',
                    params: { search: search ?? '', orderBy: orderBy ?? '', filters: JSON.stringify(filters ?? []) }
                };
            },
            transformResponse: (response: ApiResponse<string>) => response
        }),
        getArchivedTicketingExport: build.mutation<ApiResponse<string>, { search?: string; orderBy?: string; filters?: ActiveFilter[] }>({
            query: ({ search, orderBy, filters }) => {
                return {
                    url: `/member/ticketings/archived/export`,
                    method: 'GET',
                    params: { search: search ?? '', orderBy: orderBy ?? '', filters: JSON.stringify(filters ?? []) }
                };
            },
            transformResponse: (response: ApiResponse<string>) => response
        }),
        getTicketings: build.mutation<
            ApiPaginationResponse<TicketingTable>,
            { limit?: number; page?: number; search?: string; orderBy?: string; filters?: ActiveFilter[] }
        >({
            query: ({ limit = 10, page = 1, search, orderBy, filters }) => {
                return {
                    url: `/member/ticketings`,
                    method: 'GET',
                    params: { limit, page, search: search ?? '', orderBy: orderBy ?? '', filters: JSON.stringify(filters ?? []) }
                };
            },
            transformResponse: (response: ApiPaginationResponse<TicketingTable>) => response
        }),
        getTicketing: build.mutation<ApiResponse<Ticketing>, { id: string }>({
            query: ({ id }) => {
                return {
                    url: `/member/ticketings/${id}`,
                    method: 'GET'
                };
            },
            transformResponse: (response: ApiResponse<Ticketing>) => response
        }),
        createTicketing: build.mutation<ApiResponse<Ticketing>, TicketingToCreate>({
            query: (ticketing: TicketingToCreate) => {
                return {
                    url: `/member/ticketings`,
                    method: 'POST',
                    body: ticketing
                };
            },
            transformResponse: (response: ApiResponse<Ticketing>) => response
        }),
        createTicketings: build.mutation<ApiResponse<Ticketing[]>, TicketingToCreate[]>({
            query: (ticketings: TicketingToCreate[]) => {
                return {
                    url: `/member/ticketings/multi`,
                    method: 'POST',
                    body: {ticketings}
                };
            },
            transformResponse: (response: ApiResponse<Ticketing[]>) => response
        }),
        updateTicketing: build.mutation<ApiResponse<Ticketing>, { id: string; ticketing: TicketingToCreate }>({
            query: ({ id, ticketing }) => {
                return {
                    url: `/member/ticketings/${id}`,
                    method: 'PUT',
                    body: ticketing
                };
            },
            transformResponse: (response: ApiResponse<Ticketing>) => response
        }),
        toggleArchiveTicketings: build.mutation<ApiResponse<Ticketing>, { id: string | number; archived: boolean }>({
            query: ({ id, archived }) => {
                return {
                    url: `/member/ticketings/${id}/archive`,
                    method: 'PUT',
                    body: { archived }
                };
            },
            transformResponse: (response: ApiResponse<Ticketing>) => response
        }),
        getArchivedTicketings: build.mutation<
            ApiPaginationResponse<TicketingTable>,
            { limit?: number; page?: number; search?: string; orderBy?: string; filters?: ActiveFilter[] }
        >({
            query: ({ limit = 10, page = 1, search, orderBy, filters }) => {
                return {
                    url: `/member/ticketings/archived`,
                    method: 'GET',
                    params: { limit, page, search: search ?? '', orderBy: orderBy ?? '', filters: JSON.stringify(filters ?? []) }
                };
            },
            transformResponse: (response: ApiPaginationResponse<TicketingTable>) => response
        })
    })
});

export const {
    useGetTicketingEditionMutation,
    useGetTicketingEditionExportMutation,
    useGetTicketingsMutation,
    useGetTicketingMutation,
    useGetArchivedTicketingsMutation,
    useCreateTicketingMutation,
    useCreateTicketingsMutation,
    useUpdateTicketingMutation,
    useToggleArchiveTicketingsMutation,
    useGetTicketingExportMutation,
    useGetArchivedTicketingExportMutation
} = ticketingApi;
